import { Table, TablePaginationConfig, TableProps } from 'antd';
import { useGetComplications } from '../../services/api/complications';
import {
  Complication,
  ComplicationCategory,
  ComplicationStatusWithAll,
  ComplicationType,
} from '../../dto/Complication.dto';
import { ReactNode, useEffect, useState } from 'react';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import {
  ComplicationSlotType,
  WFComplicationType,
} from '../../dto/WatchFace.dto';

export interface TableColumn<T> {
  title: ReactNode;

  render: (v: T) => ReactNode;

  filters?: { text: string; value: string }[];

  filterMultiple?: boolean;
  
  filteredValue?: any;

  key?: string;
}

interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: SorterResult<any>['field'];
  sortOrder?: SorterResult<any>['order'];
  filters?: any;
}

const typeFilterOptions = [
  { text: 'Admin', value: ComplicationType.ADMIN },
  { text: 'Customer', value: ComplicationType.CUSTOMER },
];

const statusFilterOptions = [
  { text: 'All', value: ComplicationStatusWithAll.ALL },
  { text: 'Draft', value: ComplicationStatusWithAll.DRAFT },
  { text: 'Publish', value: ComplicationStatusWithAll.PUBLISH },
  { text: 'Deleted', value: ComplicationStatusWithAll.DELETED },
];

const slotFilterOptions = [
  { text: 'Bezel', value: ComplicationSlotType.Bezel },
  { text: 'Bottom', value: ComplicationSlotType.Bottom },
  { text: 'Bottom Center', value: ComplicationSlotType.BottomCenter },
  { text: 'Bottom Left', value: ComplicationSlotType.BottomLeft },
  { text: 'Bottom Right', value: ComplicationSlotType.BottomRight },
  { text: 'Center', value: ComplicationSlotType.Center },
  { text: 'Slot1', value: ComplicationSlotType.Slot1 },
  { text: 'Slot2', value: ComplicationSlotType.Slot2 },
  { text: 'Slot3', value: ComplicationSlotType.Slot3 },
  { text: 'Subdial Bottom', value: ComplicationSlotType.SubdialBottom },
  { text: 'Subdial Left', value: ComplicationSlotType.SubdialLeft },
  { text: 'Subdial Right', value: ComplicationSlotType.SubdialRight },
  { text: 'Subdia lTop', value: ComplicationSlotType.SubdialTop },
  { text: 'Top', value: ComplicationSlotType.Top },
  { text: 'Top Left', value: ComplicationSlotType.TopLeft },
];

const complicationTypeFilterOptions = [
  { text: 'California', value: WFComplicationType.California },
  { text: 'Infograph', value: WFComplicationType.Infograph },
  { text: 'Infograph Modular', value: WFComplicationType.InfographModular },
  { text: 'Meridian', value: WFComplicationType.Meridian },
  { text: 'Modular Compact', value: WFComplicationType.ModularCompact },
  { text: 'Modular Duo', value: WFComplicationType.ModularDuo },
  { text: 'XLarge', value: WFComplicationType.XLarge },
];

const categoryFilterOptions = [
  { text: 'Activity', value: ComplicationCategory.Activity },
  { text: 'Battery', value: ComplicationCategory.Battery },
  { text: 'DateAndTime', value: ComplicationCategory.DateAndTime },
  { text: 'Distance', value: ComplicationCategory.Distance },
  { text: 'Exercise', value: ComplicationCategory.Exercise },
  { text: 'Image', value: ComplicationCategory.Image },
  { text: 'Miscellaneous', value: ComplicationCategory.Miscellaneous },
  { text: 'Pulse', value: ComplicationCategory.Pulse },
  { text: 'Stand', value: ComplicationCategory.Stand },
  { text: 'Steps', value: ComplicationCategory.Steps },
  { text: 'Weather', value: ComplicationCategory.Weather },
];

interface ComplicationTableProps {
  search?: string;
  onClick: (record: Complication) => void;
}

export function ComplicationTable(props: ComplicationTableProps) {
  const { search, onClick } = props;

  const [tableParams, setTableParams] = useState<TableParams>({
    pagination: {
      current: 1,
      pageSize: 5,
      position: ['bottomCenter', 'topCenter'],
    },
    filters: {
      status: [ComplicationStatusWithAll.ALL],
    },
  });

  const { data: complications, isLoading } = useGetComplications({
    page: tableParams.pagination!.current!,
    limit: tableParams.pagination!.pageSize!,
    ...tableParams.filters,
    search,
  });

  useEffect(() => {
    if (!complications) return;

    setTableParams((prev) => ({
      ...prev,
      pagination: {
        ...prev.pagination,
        total: complications.resultsCount,
        current: complications.currentPage,
      },
    }));
  }, [complications]);

  return (
    <Table<Complication>
      loading={isLoading}
      dataSource={complications?.data}
      columns={getColumns()}
      onChange={handleTableChange}
      pagination={tableParams.pagination}
      onRow={(record) => ({
        onClick: () => onClick(record),
      })}
    />
  );

  function handleTableChange(
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Complication> | SorterResult<Complication>[]
  ) {
    setTableParams({
      pagination,
      filters,
      sortOrder: Array.isArray(sorter) ? undefined : sorter.order,
      sortField: Array.isArray(sorter) ? undefined : sorter.field,
    });
  }
}

function getColumns(): TableColumn<Complication>[] {
  return [
    {
      title: 'Image',
      render: (el) => (
        <div className='w-20 h-20 rbg-slate-400 flex items-center justify-center text-sm'>
          <img
            src={el.previewUrl}
            alt={el.previewUrl}
            className={'w-20 h-20 object-contain'}
          />
        </div>
      ),
    },
    {
      title: 'Name',
      render: (el) => el.name || ' - ',
    },
    {
      title: 'Status',
      key: 'status',
      render: (el) => el.status,
      filters: statusFilterOptions,
      filterMultiple: false,
    },
    {
      title: 'Type',
      key: 'type',
      render: (el) => el.type,
      filters: typeFilterOptions,
      filterMultiple: false,
    },
    {
      title: 'Slot',
      key: 'slot',
      render: (el) => el.slot,
      filters: slotFilterOptions,
      filterMultiple: true,
    },
    {
      title: 'Complication Types',
      key: 'complicationType',
      render: (el) => el.supportedComplicationTypes.join(', '),
      filters: complicationTypeFilterOptions,
      filterMultiple: true,
    },
    {
      title: 'Categories',
      key: 'category',
      render: (el) => el.categories.join(', '),
      filters: categoryFilterOptions,
      filterMultiple: true,
    },
    {
      title: 'User',
      render: (el) => el.user?.username,
    },
  ];
}
